import { Component, h, Prop, State, Event, EventEmitter, Listen, Watch, Element } from '@stencil/core';
import '../../../stories/Components/ChatSidebar/ChatSidebar';
import ChatSDK from 'test-iso-chat-sdk';
import '../../../stories/Components/Toaster/Toaster';
import '../../../stories/Components/Loader/Spinner/Spinner';
import { showToast } from '../../../stories/Components/Toaster/toast-helper';
import { GROUP_PROFILE_PLACEHOLDER_IMAGE } from '../../../constants/urls';
import { conversationStore } from '../../../store/conversation';
import { drawerDialogService } from '../../../stories/Components/Dialog-Drawer/dialog-drawer.js';
import { postGroupCast } from '../../../api/groupcast.js';

@Component({
  tag: 'chat-side-bar',
  shadow: true,
  styles: `
    .loader {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px;
      border-radius: 5px;
      z-index: 10000;
      height: 100%;
      width: 100%;
      background: rgb(0, 0, 0, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .toaster {
      position: fixed;
      bottom: 20px;
      right: 20px;

      z-index: 1000;
    }
  `,
})
class ChatSidebar {
  @Element() el: HTMLElement;

  //props
  @Prop() isMqttConnected: boolean;
  @Prop() conversationList: any = [];
  @Prop() isChatFilter: boolean;
  @Prop() isAllChatButton: boolean;
  @Prop() customTypes: any = {};
  @Prop() filterTabsContainer: any = null;
  @Prop() filterTab: any = null;
  @Prop() filterTabHover: any = null;
  @Prop() isSidebarHeaderLabel: boolean;
  @Prop() isSidebarHeaderSearch: boolean;
  @Prop() sidebarHeaderLabel: string;
  @Prop() sidebarColor: string;
  @Prop() sidebarSelectedColor: string;
  @Prop() onDeleteConversation: (conversationId: string) => void;
  @Prop() handleSearchData: (searchTag: string) => void;
  @Prop() userMetaData: any;
  @Prop() isTyping: boolean;
  @Prop() typingUserName: String;
  @Prop() typingConversationId: string;
  @Prop() allMessages: any; 
  @Prop() leofferAccountId: string;
  @Prop() isPreloadingForFilter: boolean;
  @Prop() isConversationLoading: Boolean;
  @Prop() hasMoreConversations: boolean;
  @Prop() unreadConversationsCount: number;
  @Prop({ mutable: true }) conversationDetails: any;
  @Prop() handleBackButtonClick?: Function;

  //state
  @State() sortedConversationList: any = [];
  @State() numberOfConversations: number = 0;
  @State() unreadCounts: { [key: string]: number } = {};
  @State() searchQuery: string = '';
  @State() selectedConversationID: string | null = null;
  @State() isLoading: boolean = false;
  @State() nonBlockedContacts: any[] = [];
  @State() skip: number = 0;
  @State() isNoContactsFound: boolean = false;
  @State() isGroupChat: boolean = false;
  @State() selectedGroupUserIds: string[] = [];
  @State() groupInfo: { title: string; imageUrl: string } = { title: '', imageUrl: '' };
  @State() isGroupCreationLoading: boolean = false;
  @State() displayTitle: string = '';
  @State() searchTagName: string = '';
  @State() hasMoreMembers: boolean = false;
  @State() isLoadingMember: boolean = false;
  @State() isPaginationMember: boolean = false;
  @State() filteredConversationList: any[] = [];
  @State() activeTab: string = 'all';
  //events
  @Event() chatTileSelected: EventEmitter;
  @Event() groupExited: EventEmitter;

  componentWillLoad() {
    this.numberOfConversations = this.conversationList.length;
    if (this.conversationDetails) {
      conversationStore.updateConversationDetails({
        conversationTitle: this.conversationDetails.isGroup ? this.conversationDetails.conversationTitle : this.conversationDetails.opponentDetails?.userName,
        // @ts-ignore
        conversationId: this.conversationDetails.conversationId,
        isGroup: this.conversationDetails.isGroup,
        conversationImageUrl: this.conversationDetails.isGroup ? this.conversationDetails.conversationImageUrl : this.conversationDetails.opponentDetails?.userProfileImageUrl,
        opponentDetails: this.conversationDetails.opponentDetails,
      });
    }
    // Initialize unread counts and sorted list
    this.unreadCounts = this.conversationList.reduce((acc, conversation) => {
      acc[conversation.conversationId] = conversation.unreadMessagesCount || 0;
      return acc;
    }, {});

    this.sortConversations();
    this.filterConversations();
  }

  @Watch('conversationList')
  handleConversationListChange() {
    this.sortConversations();
    this.filterConversations();

    if (this.selectedConversationID) {
      this.unreadCounts = {
        ...this.unreadCounts,
        [this.selectedConversationID]: 0,
      };
    }
  }

  @Watch('conversationDetails')
  handleConversationDetailsChange(newValue: any) {
    if (newValue) {
      conversationStore.updateConversationDetails({
        conversationTitle: newValue.isGroup ? newValue.conversationTitle : newValue.opponentDetails?.userName,
        // @ts-ignore
        conversationId: newValue.conversationId,
        isGroup: newValue.isGroup,
        conversationImageUrl: newValue.isGroup ? newValue.conversationImageUrl : newValue.opponentDetails?.userProfileImageUrl,
        opponentDetails: newValue.opponentDetails,
      });
    }
  }
  sortConversations() {
    this.sortedConversationList = [...this.conversationList].sort((a, b) => {
      if (
        (a?.lastMessageDetails?.action == 'chatMessageSent' || a?.lastMessageDetails?.action == 'forward') &&
        (a?.lastMessageDetails?.body === '' || a?.lastMessageDetails?.action === 'clearConversation')
      )
        return;
      if (
        (b?.lastMessageDetails?.action == 'chatMessageSent' || b?.lastMessageDetails?.action == 'forward') &&
        (b?.lastMessageDetails?.body === '' || b?.lastMessageDetails?.action === 'clearConversation')
      )
        return;

      const timeA =
        a?.lastMessageDetails?.action === 'membersAdd' ? a?.lastMessageDetails?.sentAt : a?.lastMessageDetails?.updatedAt || a?.lastMessageDetails?.sentAt || a?.updatedAt || 0;

      const timeB =
        b?.lastMessageDetails?.action === 'membersAdd' ? b?.lastMessageDetails?.sentAt : b?.lastMessageDetails?.updatedAt || b?.lastMessageDetails?.sentAt || b?.updatedAt || 0;

      return timeB - timeA;
    });
  }

  @Listen('group-contacts-input-changed')
  handleGroupContactsInputChange(event: CustomEvent) {
    const { inputValue, isDebounced, selectedUserIds } = event.detail;

    // Reset pagination
    this.skip = 0;
    this.nonBlockedContacts = [];
    this.isNoContactsFound = false;
    this.searchTagName = inputValue;
    // For empty input or non-debounced events (like clear), fetch immediately
    if (inputValue?.trim() === '' || !isDebounced) {
      this.getAllGroupMemeber('', undefined, selectedUserIds);
      return;
    }

    // For debounced search with input
    if (inputValue?.trim()?.length > 0) {
      this.getAllGroupMemeber(inputValue, undefined, selectedUserIds);
    }
  }

  @Listen('new-chat-input-changed')
  handleNewChatInputChange(event: CustomEvent) {
    const { inputValue } = event.detail;
    this.skip = 0;
    this.nonBlockedContacts = [];
    this.isNoContactsFound = false;

    if (inputValue?.trim() === '') {
      this.getAllMemeber('');
      return;
    }
    if (inputValue?.trim()?.length > 0) {
      this.getAllMemeber(inputValue);
    }
  }

  @Listen('close-group-member-list')
  handleCloseGroupMemberList() {
    this.isPaginationMember = false;
    this.isLoadingMember = false;
    this.nonBlockedContacts = [];
    this.searchTagName = '';
    this.hasMoreMembers = false;
    this.getAllMemeber('');
  }
  @Listen('unreadMessageCountUpdated')
  handleUnreadMessageCountUpdate(event: CustomEvent) {
    const { conversationId, count, updatedConversation, allConversations, isReset } = event.detail;

    // Handle both increment and reset cases
    if (count > 0 || isReset) {
      const currentCount = this.unreadCounts[conversationId] || 0;
      const newCount = isReset ? 0 : currentCount + count;
      
      this.unreadCounts = {
        ...this.unreadCounts,
        [conversationId]: newCount,
      };

      // If we have the updated conversation data from the main component, use it
      if (updatedConversation) {
        this.conversationList = this.conversationList.map(conversation => {
          if (conversation.conversationId === conversationId) {
            return {
              ...updatedConversation,
              unreadMessagesCount: newCount,
              lastMessageDetails: {
                ...updatedConversation.lastMessageDetails,
                updatedAt: Date.now(),
              },
            };
          }
          return conversation;
        });
      } else {
        // Fallback to the old method if no updated conversation data
        this.conversationList = this.conversationList.map(conversation => {
          if (conversation.conversationId === conversationId) {
            return {
              ...conversation,
              unreadMessagesCount: newCount,
              lastMessageDetails: {
                ...conversation.lastMessageDetails,
                updatedAt: Date.now(),
              },
            };
          }
          return conversation;
        });
      }

      // If we have all conversations data, sync the entire list to ensure consistency
      if (allConversations && allConversations.length > 0) {
        this.conversationList = allConversations.map(conv => {
          const existingConv = this.conversationList.find(c => c.conversationId === conv.conversationId);
          return existingConv ? { ...existingConv, ...conv } : conv;
        });
      }

      this.sortConversations(); // Ensure conversations are sorted after update
      
      if (isReset) {
        console.log(`🔄 Sidebar: Unread count reset for conversation ${conversationId}`);
      } else {
        console.log(`📬 Sidebar: Unread count updated for conversation ${conversationId} to ${newCount}`);
      }
    }
  }

  handleChatTileSelected(event: CustomEvent) {
    const selectedConversation = event.detail;
    const selectedConvoId = selectedConversation.conversationId;

    this.selectedConversationID = selectedConvoId;

    this.conversationList = this.sortedConversationList.map(conversation => {
      if (conversation.conversationId === selectedConvoId) {
        return {
          ...conversation,
          unreadMessagesCount: 0,
        };
      }
      return conversation;
    });
    this.unreadCounts[selectedConvoId] = 0;
    this.chatTileSelected.emit(selectedConvoId);
  }

  handleSearch(e: Event) {
    const input = e.target as HTMLInputElement;
    this.searchQuery = input?.value?.toLowerCase();
  }
   
  

  @Listen('delete-conversation')
  handleDeleteConversation(event: CustomEvent) {
    const { conversationId } = event.detail;
    this.onDeleteConversation(conversationId);
  }

  @Listen('new-chat-contact-selected')
  handleNewChatContactSelected(event: CustomEvent) {
    const { userName, userId } = event.detail;
    this.createConversation(userId, userName);
  }

  @Listen('new-chat-clicked')
  handleNewChatClick() {
    this.getAllMemeber();
  }

  getAllMemeber = async (searchTag = '', skip = 0) => {
    if (skip === 0) {
      this.isLoadingMember = true;
      this.nonBlockedContacts = [];
    }

    if (skip >= 20) {
      this.isPaginationMember = true;
    }
    this.hasMoreMembers = true;

    try {
      this.skip = skip;

      const paramsForNonBlockedContacts = {
        sort: 1,
        limit: 20,
        skip: skip,
        context: 'new_chat',
      };

      if (searchTag?.trim()) {
        // @ts-ignore
        paramsForNonBlockedContacts.searchTag = searchTag;
      }

      const res = await ChatSDK.getInstance().user.getUserNonblock(paramsForNonBlockedContacts);

      if (res?.users && res.users.length > 0) {
        this.isNoContactsFound = false;
        if (skip === 0) {
          this.nonBlockedContacts = [...res.users];
        } else {
          this.nonBlockedContacts = [...this.nonBlockedContacts, ...res.users];
        }

        if (res.users.length < paramsForNonBlockedContacts.limit) {
          this.hasMoreMembers = false;
        }
      } else {
        if (searchTag?.trim()) {
          this.isNoContactsFound = true;
        }
        this.hasMoreMembers = false;
      }
    } catch (error) {
      console.error(error, 'error from get all non blocked contacts');
      this.isNoContactsFound = true;
      this.hasMoreMembers = false;
    } finally {
      this.isLoadingMember = false;
      this.isPaginationMember = false;
    }
  };

  getAllGroupMemeber = async (searchTag = '', skip = 0, selectedUserIds = []) => {
    if (skip === 0) {
      this.isLoadingMember = true;
      this.nonBlockedContacts = [];
    }

    if (skip >= 20) {
      this.isPaginationMember = true;
    }
    this.hasMoreMembers = true;

    try {
      this.skip = skip;

      const paramsForNonBlockedContacts = {
        sort: 1,
        limit: 20,
        skip: skip,
        context: 'group_chat',
      };

      if (searchTag?.trim()) {
        // @ts-ignore
        paramsForNonBlockedContacts.searchTag = searchTag;
      }

      const res = await ChatSDK.getInstance().user.getUserNonblock(paramsForNonBlockedContacts);

      if (res?.users && res.users.length > 0) {
        this.isNoContactsFound = false;

        // Filter out selected users from the results
        const filteredUsers = res.users.filter(user => !selectedUserIds?.includes(user.userId));

        if (skip === 0) {
          this.nonBlockedContacts = [...filteredUsers];
        } else {
          // Add only unique contacts that aren't already in the list
          const uniqueContacts = filteredUsers.filter(newContact => !this.nonBlockedContacts.some(existingContact => existingContact.userId === newContact.userId));
          this.nonBlockedContacts = [...this.nonBlockedContacts, ...uniqueContacts];
        }

        if (res.users.length < paramsForNonBlockedContacts.limit) {
          this.hasMoreMembers = false;
        }
      } else {
        if (searchTag?.trim()) {
          this.isNoContactsFound = true;
        }
        this.hasMoreMembers = false;
      }
    } catch (error) {
      console.error(error, 'error from get all non blocked contacts');
      this.isNoContactsFound = true;
      this.hasMoreMembers = false;
    } finally {
      this.isLoadingMember = false;
      this.isPaginationMember = false;
    }
  };

  async createConversation(selectedContactId: string, selectedContactName: string) {
    try {
      const paramsForCreateConversation = {
        typingEvents: true,
        readEvents: true,
        pushNotifications: true,
        members: [selectedContactId],
        isGroup: false,
        conversationType: 0,
        searchableTags: [selectedContactName],
      };
      const res = await ChatSDK.getInstance().Conversation.postConversation(paramsForCreateConversation);

      if (res.conversationId) {
        this.sortConversations();

        this.selectedConversationID = res.conversationId;

        // Update URL and emit events
        const url = new URL(window.location.href);
        url.searchParams.set('conversation', res.conversationId);
        window.history.pushState({}, '', url);

        this.chatTileSelected.emit(res.conversationId);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  }

  @Listen('new-chat-tile-list-scrolled-to-bottom')
  @Listen('group-contacts-scrolled-to-bottom')
  handleNewChatTileListScrolledToBottom() {
    if (this.isGroupChat) {
      this.getAllGroupMemeber(this.searchTagName, (this.skip += 20));
    } else {
      this.getAllMemeber(this.searchTagName, (this.skip += 20));
    }
  }

  @Listen('create-group-clicked')
  @Listen('create-groupcast-clicked')
  handleCreateGroupClick(event: CustomEvent) {
    const { isGroup } = event.detail;
    this.isGroupChat = isGroup;
    this.nonBlockedContacts = [];
    this.isLoadingMember = false;
    this.hasMoreConversations = false;
    this.isPaginationMember = false;
    this.searchTagName = '';
    this.getAllGroupMemeber('');
  }

  @Listen('group-contacts-selected')
  handleGroupContactsSelected(event: CustomEvent) {
    const { selectedUserIds } = event.detail;
    this.selectedGroupUserIds = selectedUserIds;
  }

  @Listen('group-info-submitted')
  handleGroupInfoSubmitted(event: CustomEvent) {
    const { conversationTitle, conversationImageUrl } = event.detail;
    this.groupInfo = {
      title: conversationTitle,
      imageUrl: conversationImageUrl,
    };

    // Now create the group conversation with all collected information
    this.createGroupConversation();
  }

  async createGroupConversation() {
    try {
      const paramsForCreateConversation = {
        typingEvents: true,
        readEvents: true,
        pushNotifications: true,
        members: this.selectedGroupUserIds,
        isGroup: true,
        conversationType: 0,
        conversationTitle: this.groupInfo.title,
        conversationImageUrl: this.groupInfo.imageUrl,
      };

      const res = await ChatSDK.getInstance().Conversation.postConversation(paramsForCreateConversation);

      // Add the new group conversation to the list
      const newConversation = {
        conversationId: res.conversationId,
        lastMessageDetails: {
          updatedAt: Date.now(),
          body: '',
        },
        unreadMessagesCount: 0,
        isGroup: true,
        conversationTitle: this.groupInfo.title,
        conversationImageUrl: this.groupInfo.imageUrl,
      };

      this.conversationList = [newConversation, ...this.conversationList];
      // this.sortConversations();
      this.selectedConversationID = res.conversationId;

      // Update URL and emit events
      const url = new URL(window.location.href);
      url.searchParams.set('conversation', res.conversationId);
      window.history.pushState({}, '', url);

      this.chatTileSelected.emit(res.conversationId);
    } catch (error) {
      console.error('Error creating group conversation:', error);
    }
  }

  getPresignedUrl = async (file: File) => {
    try {
      const mediaExtension = file.name.split('.').pop();
      const res = await ChatSDK.getInstance().user.getPresignedurlCreate({
        userIdentifier: this.userMetaData.userIdentifier,
        mediaExtension,
      });

      // Upload file to presigned URL
      await fetch(res.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Return the final URL for the uploaded file
      return res.mediaUrl;
    } catch (error) {
      console.error('Error getting presigned URL or uploading file:', error);
      throw error;
    }
  };

  @Listen('create-group-submitted')
  @Listen('create-group-cast-submitted')
  async handleGroupSubmitted(event: CustomEvent) {
    const { conversationTitle, imageFile, selectedUserIds, isGroupCast, selectedUsers } = event.detail;
    try {
      this.isGroupCreationLoading = true;

      let conversationImageUrl = '';
      if (imageFile) {
        // @ts-ignore
        conversationImageUrl = await this.getPresignedUrl(imageFile);
      } else {
        conversationImageUrl = GROUP_PROFILE_PLACEHOLDER_IMAGE;
      }

      const paramsForCreateConversation = {
        typingEvents: true,
        readEvents: true,
        pushNotifications: true,
        members: selectedUserIds,
        isGroup: true,
        conversationType: 1,
        conversationTitle,
        conversationImageUrl,
        searchableTags: [conversationTitle],
      };

      const members = selectedUserIds.map(id => ({
        newConversationTypingEvents: true,
        newConversationReadEvents: true,
        newConversationPushNotificationsEvents: true,
        newConversationMetadata: {},
        memberId: id,
        newConversationCustomType: 'Groupcast',
        userName: id.userName
      }));
      const paramsForCreateGroupCastConversation = {
        groupcastTitle: conversationTitle,
        groupcastImageUrl: conversationImageUrl,
        searchableTags: [conversationTitle],
        metaData: { description: 'groupcast created',
          members: selectedUsers
         },
        customType: 'groupcast',
        members,
      };

      if (isGroupCast) {
        const res = await postGroupCast(paramsForCreateGroupCastConversation);
        this.selectedConversationID = res.groupcastId;
        const url = new URL(window.location.href);
        url.searchParams.set('groupcast', res.groupcastId);
        window.history.pushState({}, '', url);
        this.chatTileSelected.emit({groupcastId: res.groupcastId});


      } else {
        const res = await ChatSDK.getInstance().Conversation.postConversation(paramsForCreateConversation);
        const newConversation = {
          conversationId: res.conversationId,
          lastMessageDetails: {
            updatedAt: Date.now(),
            body: '',
          },
          unreadMessagesCount: 0,
          isGroup: true,
          conversationTitle,
          conversationImageUrl,
          members: selectedUserIds,
        };
        this.conversationList = [newConversation, ...this.conversationList];
        // this.sortConversations();
        this.selectedConversationID = res.conversationId;
  
        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set('conversation', res.conversationId);
        window.history.pushState({}, '', url);
  
        // Emit chat tile selected event
        this.chatTileSelected.emit(res.conversationId);
        showToast({
          message: 'Group created successfully',
          type: 'success',
          position: 'bottom-right',
        });
      }
    
    } catch (error) {
      console.error('Error creating group conversation:', error);
      // Show error toaster
      showToast({
        message: 'Failed to create group',
        type: 'error',
        position: 'bottom-right',
      });
    } finally {
      this.isGroupCreationLoading = false;
    }
  }

  @Listen('exit-group')
  async handleExitGroup(event: CustomEvent) {
    const conversationId = event.detail?.conversationId;
    if (!conversationId) {
      console.error('No conversation ID received in exit-group event');
      return;
    }
    await this.exitGroup(conversationId);
  }

  @Listen('contact-action', { target: 'body' })
  async handleContactAction(event: CustomEvent) {
    const { action, conversationId } = event.detail;
    if (action === 'exit-group') {
      if (!conversationId) {
        console.error('No conversation ID provided for exit-group action');
        return;
      }
      await this.exitGroup(conversationId);
    }
  }

  private async exitGroup(conversationId: string) {
    try {
      this.isLoading = true;

      await ChatSDK.getInstance().Conversation.deleteConversationLeave({ conversationId });

      this.conversationList = this.conversationList.filter(conversation => conversation.conversationId !== conversationId);

      // this.sortConversations();
      const url = new URL(window.location.href);
      url.searchParams.delete('conversation');
      window.history.replaceState({}, '', url.toString());
      showToast({
        message: 'Successfully exited group',
        type: 'success',
        position: 'bottom-right',
      });
      this.groupExited.emit({ conversationId });
    } catch (error) {
      console.error('Error exiting group:', error);
      showToast({
        message: 'Failed to exit group',
        type: 'error',
        position: 'bottom-right',
      });
    } finally {
      this.isLoading = false;
      drawerDialogService.closeDialog('confirm-dialog');
    }
  }

  @Listen('message-read-status-updated')
  handleMessageReadStatusUpdate(event: CustomEvent) {
    const { conversationId, isRead, isDelivered } = event.detail;

    // Update the conversation list to reflect new read status
    this.conversationList = this.conversationList.map(conversation => {
      if (conversation.conversationId === conversationId) {
        return {
          ...conversation,
          lastMessageDetails: {
            ...conversation.lastMessageDetails,
            readByAll: isRead,
            deliveredToAll: isDelivered,
          },
        };
      }
      return conversation;
    });
  }



  filterConversations() {
    if (!this.conversationList) {
      this.filteredConversationList = [];
      return;
    }
    switch (this.activeTab) {
      case 'all':
        this.filteredConversationList = this.conversationList;
        break;
      case 'buying':
        this.filteredConversationList = this.conversationList.filter(conversation => this.leofferAccountId === conversation?.metaData?.buyerAccountId);
        break;
      case 'selling':
        this.filteredConversationList = this.conversationList.filter(conversation => this.leofferAccountId === conversation?.metaData?.sellerAccountId);
        break;
      default:
        this.filteredConversationList = this.conversationList;
        break;
    }
  }

  render() {
    return (
      <div class="chat-sidebar-container" style={{ height: '100%' }}>
        {this.isGroupCreationLoading && (
          <div class="loader">
            <spinner-loader
              height="30px"
              width="30px"
              spinTime="1s"
              spinnerPrimaryColor="var(--bg-primary-color)"
              spinnerSecondaryColor="var(--text-primary-color)"
              spinnerBorderWidth="2px"
            ></spinner-loader>
          </div>
        )}

        <chat-sidebar
          isMqttConnected={this.isMqttConnected}
          unreadConversationsCount={this.unreadConversationsCount}
          hasMoreConversations={this.hasMoreConversations}
          typingConversationId={this.typingConversationId}
          nonBlockedContacts={this.nonBlockedContacts}
          isChatFilter={this.isChatFilter}
          conversationData={this.filteredConversationList}
          onChatTileSelected={(event: CustomEvent) => this.handleChatTileSelected(event)}
          unreadMessageCount={undefined}
          customTypes={this.customTypes}
          isAllChats={this.isAllChatButton}
          filterTabsContainer={this.filterTabsContainer}
          displayTitle={this.displayTitle}
          filterTab={this.filterTab}
          filterTabHover={this.filterTabHover}
          filterTabActive={{
            borderBottom: '2px solid var(--bg-primary-light)'
          }}
          isSidebarHeaderSearch={this.isSidebarHeaderSearch}
          isSidebarHeaderLabel={this.isSidebarHeaderLabel}
          sidebarHeaderLabel={this.sidebarHeaderLabel}
          sidebarColor={this.sidebarColor}
          sidebarSelectedColor={this.sidebarSelectedColor}
          isNoContactsFound={this.isNoContactsFound}
          isLoading={this.isLoading}
          isGroupCreationLoading={this.isGroupCreationLoading}
          handleSearchData={(serachText: string) => this.handleSearchData(serachText)}
          isTyping={this.isTyping}
          typingUserName={this.typingUserName}
          allMessages={this.allMessages}
          userMetaData={this.userMetaData}
          leofferAccountId={this.leofferAccountId}
          isPreloadingForFilter={this.isPreloadingForFilter}
          isConversationLoading={this.isConversationLoading}
          isPaginationMember={this.isPaginationMember}
          isLoadingMember={this.isLoadingMember}
          hasMoreMembers={this.hasMoreMembers}
          handleBackButtonClick={this.handleBackButtonClick}
        ></chat-sidebar>
      </div>
    );
  }
}

export { ChatSidebar };
